import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { DropdownOption } from '@design-system/index';
import { useGetApartmentsMutation } from '@services/member/apartment/apartment.endpoints';
import { ApartmentTable } from '@services/member/apartment/apartment.types';
import { BookingStatus, BookingTable } from '@services/member/booking/booking.types';
import { PaginationMetadata } from '@services/providers/cosem.types';

export const ALL_APARTMENTS_OPTION: DropdownOption = { id: 0, label: 'Tous les logements' };

const BOOKING_STATUSES: BookingStatus[] = ['upcoming', 'current', 'past', 'all'];

const apartmentLabel = (apartment: ApartmentTable) => `${apartment.label} (${apartment.reference})`;

const parseStatus = (value: string | null): BookingStatus =>
    BOOKING_STATUSES.includes(value as BookingStatus) ? (value as BookingStatus) : 'upcoming';

const parseApartmentId = (value: string | null): number | null => {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * L'état de la vue (statut, logement) vit dans l'URL : un rechargement, le bouton
 * "Retour" du formulaire de réservation ou un lien partagé retombent sur la même vue.
 */
export const useListPageVM = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const columnHelper = createColumnHelper<BookingTable>();

    const status = parseStatus(searchParams.get('status'));
    const apartmentId = parseApartmentId(searchParams.get('apartmentId'));

    const [total, setTotal] = useState<number>(0);
    const [apartments, setApartments] = useState<ApartmentTable[]>([]);
    const [isNewBookingModalOpen, setIsNewBookingModalOpen] = useState<boolean>(false);
    const [newBookingApartment, setNewBookingApartment] = useState<DropdownOption | null>(null);
    const [newBookingError, setNewBookingError] = useState<string | null>(null);

    const [
        getApartments,
        { isLoading: isApartmentsLoading, isError: isApartmentsError, isSuccess: isApartmentsSuccess, data: apartmentsResponse }
    ] = useGetApartmentsMutation();

    // Logements actifs : sélecteur de filtre et choix du logement à la création
    useEffect(() => {
        getApartments({ limit: 200, page: 1, orderBy: 'label:asc' });
    }, [getApartments]);

    useEffect(() => {
        if (isApartmentsSuccess && apartmentsResponse) {
            setApartments(apartmentsResponse.data.results ?? []);
        }
    }, [isApartmentsSuccess, apartmentsResponse]);

    // Le compteur repart de zéro à chaque changement de vue, comme la liste des logements
    useEffect(() => {
        setTotal(0);
    }, [status, apartmentId]);

    const updateSearchParams = useCallback(
        (changes: { status?: BookingStatus; apartmentId?: number | null }) => {
            const next = new URLSearchParams(searchParams);
            if (changes.status !== undefined) {
                if (changes.status === 'upcoming') next.delete('status');
                else next.set('status', changes.status);
            }
            if (changes.apartmentId !== undefined) {
                if (changes.apartmentId) next.set('apartmentId', String(changes.apartmentId));
                else next.delete('apartmentId');
            }
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const setStatus = useCallback((value: BookingStatus) => updateSearchParams({ status: value }), [updateSearchParams]);

    const handleApartmentChange = useCallback(
        (option: DropdownOption | null) => {
            const id = option ? Number(option.id) : 0;
            updateSearchParams({ apartmentId: id > 0 ? id : null });
        },
        [updateSearchParams]
    );

    const apartmentChoices = useMemo<DropdownOption[]>(
        () => apartments.map((apartment) => ({ id: apartment.id, label: apartmentLabel(apartment) })),
        [apartments]
    );

    const apartmentOptions = useMemo<DropdownOption[]>(() => [ALL_APARTMENTS_OPTION, ...apartmentChoices], [apartmentChoices]);

    const handleMetadata = useCallback((metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotal(metadata.total);
        }
    }, []);

    // Chemin de retour transmis au formulaire de réservation pour revenir sur cette vue
    const backTo = location.pathname + location.search;

    const goToBooking = useCallback(
        (booking: BookingTable) => {
            if (!booking.apartment) return;
            navigate(`/members-management/apartments/${booking.apartment.id}/bookings/${booking.id}/edit`, { state: { backTo } });
        },
        [navigate, backTo]
    );

    const goToApartmentPlanning = useCallback(
        (apartmentIdToOpen: number) => navigate(`/members-management/apartments/${apartmentIdToOpen}/details/planning`),
        [navigate]
    );

    const goToPlanning = useCallback(() => navigate('/members-management/apartments/edition'), [navigate]);

    // Une réservation se crée toujours depuis un logement : on demande lequel.
    const openNewBookingModal = useCallback(() => {
        setNewBookingApartment(null);
        setNewBookingError(null);
        setIsNewBookingModalOpen(true);
    }, []);

    const closeNewBookingModal = useCallback(() => setIsNewBookingModalOpen(false), []);

    const confirmNewBooking = useCallback(() => {
        if (!newBookingApartment) {
            setNewBookingError('Sélectionnez un logement pour continuer.');
            return;
        }
        setIsNewBookingModalOpen(false);
        navigate(`/members-management/apartments/${newBookingApartment.id}/bookings/add`, { state: { backTo } });
    }, [newBookingApartment, navigate, backTo]);

    // Arrivée depuis le tableau de bord ou une fiche ("Créer une réservation") : ouvrir directement le choix du logement
    useEffect(() => {
        if (searchParams.get('action') !== 'new' || !isApartmentsSuccess) {
            return;
        }
        const next = new URLSearchParams(searchParams);
        next.delete('action');
        setSearchParams(next, { replace: true });
        openNewBookingModal();
    }, [searchParams, setSearchParams, isApartmentsSuccess, openNewBookingModal]);

    return {
        columnHelper,
        status,
        setStatus,
        apartmentId,
        handleApartmentChange,
        apartmentOptions,
        apartmentChoices,
        isApartmentsLoading,
        isApartmentsError,
        total,
        handleMetadata,
        goToBooking,
        goToApartmentPlanning,
        goToPlanning,
        isNewBookingModalOpen,
        newBookingApartment,
        setNewBookingApartment,
        newBookingError,
        openNewBookingModal,
        closeNewBookingModal,
        confirmNewBooking
    };
};
